/**
 * 表格字段管理 Hook
 *
 * 性能优化：从已加载的记录中提取字段列表，避免额外的 Script 层调用
 */

import { useState, useEffect } from "react";
import type { FieldInfo, RecordData } from "../types";

export function useTableFields(records?: RecordData[]) {
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 从记录中提取字段列表（性能优化）
   */
  const extractFieldsFromRecords = (recordList: RecordData[]): FieldInfo[] => {
    if (!recordList || recordList.length === 0) {
      return [];
    }

    const firstRecord = recordList[0];
    const fieldNames = Object.keys(firstRecord.fields || {});

    // 使用字段名作为 ID，确保匹配时能正确取值
    const extractedFields: FieldInfo[] = fieldNames.map((name) => ({
      id: name, // 直接使用字段名作为 ID
      name: name,
      type: "text", // 默认类型，后续可以根据值推断
      isPrimary: false,
    }));

    return extractedFields;
  };

  /**
   * 加载字段列表
   */
  const loadFields = () => {
    setLoading(true);
    setError(null);

    try {
      if (records && records.length > 0) {
        const fieldList = extractFieldsFromRecords(records);
        setFields(fieldList);
      } else {
        setFields([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "提取字段失败";
      setError(errorMessage);
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  // 当记录数据变化时，重新提取字段
  useEffect(() => {
    loadFields();
  }, [records]);

  return {
    fields,
    loading,
    error,
    reloadFields: loadFields,
  };
}
